/**
 * Signed in, and waiting for an admin (`plan.md` §6.12).
 *
 * **This screen exists because the session now does.** An unapproved account
 * used to be refused a token at sign-in, which meant every reload asked the
 * person to prove who they were in order to be told the same thing again, and
 * there was nothing to sign out OF. Being made to re-authenticate to re-read a
 * "please wait" is the worst version of that message.
 *
 * Now they hold a session, stay signed in across reloads, and land here. The
 * API refuses every route but the few that let them finish or leave — see
 * `backend/src/http/approval.ts` — so this is what they see instead of an app
 * answering 403 to everything.
 *
 * ## Two affordances, and both are the point
 *
 * - **Am I in yet?** (once "Check again") is a token refresh. The approval flag rides in the access
 *   token, so an admin approving somebody does not reach a tab already open
 *   until the next one is minted. Without this they would wait out
 *   `JWT_ACCESS_TTL` wondering whether it had worked.
 * - **Sign out** is the exit. An account that cannot sign out is one whose
 *   only way to switch to another is clearing cookies.
 */
import { useState, type ReactNode } from 'react';
import { BrandLockup, Button, Stack } from '@/ui';
import { useAuth } from './AuthProvider';
import { peekReturn, takeReturn } from './ReturnAfterSignIn';
import { useTenant } from './TenantProvider';
import { isSupportHost } from '@/lib/tenant';
import { useT } from '@/i18n';

/**
 * The gate, mounted in the shell above everything an account could reach.
 *
 * Anonymous visitors pass straight through — most people who open a lineup
 * never make an account, and there is nothing to be approved for.
 *
 * **`SUPER_ADMIN` passes through too, and that mirrors the API rather than
 * being a courtesy.** `backend/src/http/approval.ts` exempts one because the
 * first admin is made from the command line on a database with nobody to
 * approve it; holding it here would show a waiting screen to the only account
 * that can let anybody in, over an API answering every request it makes.
 */
export function RequireApproval({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const { approvalOff } = useTenant();

  if (loading || !user) return <>{children}</>;
  if (user.approved || user.role === 'SUPER_ADMIN') return <>{children}</>;
  /*
   * **Switched off means nobody waits** (the user, 2026-10-09). With
   * `REQUIRE_ADMIN_APPROVAL` off the API mints every token approved
   * (`approvalOf`) and `http/approval.ts` refuses nothing — but a token from
   * before the switch, or the account this origin remembers
   * (`myrave:platform:session-user`), can still say `approved: false` until
   * the next refresh. Without this, that stale flag drew a queue that no
   * longer exists. Only on the API's own word, never on the default: an
   * unanswered `/tenant` must not open the door while the switch is on.
   */
  if (approvalOff) return <>{children}</>;
  /*
   * **An invitation to run a rave is the way IN** (the user, 2026-10-10:
   * accepting one approves the account — somebody who runs MyRave vouched for
   * that address). So its page is never behind this gate, and a sign-in that
   * set out to answer one goes back to it rather than to the waiting screen.
   * The API opens only `/admin-invitations/*` to an unapproved account.
   */
  if (window.location.pathname === '/admin-invitation') return <>{children}</>;
  // Support (round 5): somebody waiting may ask why. The API opens
  // `POST /support` to a held account too (`approval.ts`).
  if (isSupportHost() || window.location.pathname === '/support') return <>{children}</>;
  if (window.location.pathname === '/' && peekReturn() === '/admin-invitation') {
    takeReturn();
    window.location.replace('/admin-invitation');
    return null;
  }
  return <AwaitingApproval />;
}

export function AwaitingApproval() {
  const tr = useT();
  const { user, signOut, refresh } = useAuth();
  const [checking, setChecking] = useState(false);

  async function checkAgain() {
    setChecking(true);
    try {
      // A refresh RE-READS the account, which is the only thing that turns an
      // approval into a token that says so.
      await refresh();
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="app-page">
      {/*
        * **The brand, because this is a whole page and not a dialog.**
        *
        * It was a bare centred `<main>`: no logo, no name, nothing saying which
        * product was asking somebody to wait. Every other full-page screen in
        * this flow wears the lockup — `LoginPage`'s shell does, and this is the
        * screen a person lands on straight after it. Reported 2026-09-27.
        */}
      <header className="app-page-hd">
        <BrandLockup size={26} href="/" />
      </header>

      <main className="app-page-centre">
      <Stack gap={14} className="app-narrow">
        <h1 className="app-title">
          {tr('You’re on the guest list')}
        </h1>

        <p className="app-lede">
          {tr('MyRave’s doors aren’t open to everyone yet — the crew lets each new raver in by hand. You stay signed in while you wait, so there’s nothing else to do.')}
        </p>

        {/*
          * The account it is waiting ON. Somebody who has more than one — and
          * signing in with Google and with a phone makes two — otherwise has
          * no way to tell WHICH is held up.
          */}
        {(user?.email || user?.displayName) && (
          <p className="app-note">
            {tr('At the gate as {who}', { who: user.email ?? user.displayName ?? '' })}
          </p>
        )}

        <Stack gap={9} className="app-buttons">
          <Button variant="primary" onClick={checkAgain} disabled={checking}>
            {checking ? tr('Checking the list…') : tr('Am I in yet?')}
          </Button>
          <Button variant="secondary" onClick={() => void signOut()}>
            {tr('Sign out')}
          </Button>
        </Stack>
      </Stack>
      </main>
    </div>
  );
}
